import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Divider, Text, Skeleton, Link, useToast } from '@afterpaytouch/core'
import { useFlag } from '../../../../hooks'
import { useTranslation } from 'next-i18next'
import { useGetOrderTransactionPpaQuery, useLazyGetOrderTransactionPpaDocumentQuery } from '../../../../state/orders/hooks'
import { getPpaDocumentTitle } from '../../../../state/orders/utils'

const I18N_NAMESPACE = ['common', 'orders']

interface OrderSummaryLinksProps {
  orderId: string
}

export const OrderSummaryLinks: FunctionComponent<OrderSummaryLinksProps> = ({ orderId }) => {
  const { data, isFetching, isError, refetch } = useGetOrderTransactionPpaQuery(orderId)
  const isSecurePpaEnabled = useFlag('consumer-portal-ppa-link-display', false)
  const ppaDocsEndpointDrivenEnabled = useFlag('paylater.enable-ppa-docs-endpoint-driven', false)
  const { t } = useTranslation(I18N_NAMESPACE)
  const ppaDocsData = data?.ppaDocs
  const [documentId, setDocumentId] = useState<string>()
  const [getPpaDoc, { data: documentData, isError: documentError }] = useLazyGetOrderTransactionPpaDocumentQuery()
  const setToast = useToast()

  /**
   *
   * @deprecated to be removed when paylater.enable-ppa-docs-endpoint-driven flag is fulled rolled out
   * FF: 'paylater.enable-ppa-docs-endpoint-driven'
   */
  const documentTypeTranslation = {
    INSTALLMENT_AGREEMENT: t('orders:orderSummary:ppa:installmentAgreement'),
    FINAL_PAYMENT_SCHEDULE: t('orders:orderSummary:ppa:finalPaymentSchedule'),
  }

  const renderDocument = (docId: string): void => {
    setDocumentId(docId)
  }

  const refetchPpa = useCallback((): void => {
    if (isError) {
      refetch()
    }
  }, [isError, refetch])

  useEffect(() => {
    if (documentId !== undefined) {
      getPpaDoc(documentId)
    }
  }, [documentId, getPpaDoc])

  useEffect(() => {
    if (documentData?.documentId === documentId && documentId !== undefined) {
      try {
        localStorage.setItem('ppaHtml', JSON.stringify({ htmlToRender: documentData?.renderedHtml }))
      } catch (e) {}
      window.open(`ppa/${documentId}`, '_blank')
      setDocumentId(undefined)
    }
    if (documentError && Boolean(documentId)) {
      setToast({ message: t('orders:orderSummary:documentError'), kind: 'Failure' })
      setDocumentId(undefined)
    }
  }, [documentData, documentId, documentError, setToast, t])

  // @ts-ignore: OPERATION BLEED STOPPER
  return (isSecurePpaEnabled && ppaDocsData?.length > 0) || isFetching ? (
    <>
      <div className='relative'>
        <div className='relative items-center md:mt-4 md:flex md:items-center md:justify-between md:gap-x-6'>
          {ppaDocsEndpointDrivenEnabled ? (
            <>
              {ppaDocsData?.map((doc) => (
                <div className='mt-4 md:mt-0' key={`ppaLink-${doc.documentId}`}>
                  <Link onClick={() => renderDocument(doc?.documentId)} kind={'Underline'}>
                    <Text bold size={'M'} testNameSpace={'ppa-label'}>
                      {t(`orders:ppa:${getPpaDocumentTitle(doc.docType)}`)}
                    </Text>
                  </Link>
                </div>
              ))}
            </>
          ) : (
            <>
              {ppaDocsData?.map((doc) => (
                <div className='mt-4 md:mt-0' key={`ppaLink-${doc.documentId}`}>
                  <Link onClick={() => renderDocument(doc?.documentId)} kind={'Underline'}>
                    <Text bold size={'M'} testNameSpace={'ppa-label'}>
                      {documentTypeTranslation[doc.docType]}
                    </Text>
                  </Link>
                </div>
              ))}
            </>
          )}
        </div>
        {isFetching && (
          <>
            <div className='flex'>
              {Object.keys(documentTypeTranslation).map((key) => (
                <Text key={`ppaLink-loading-${key}`} bold size={'M'}>
                  {documentTypeTranslation[key]}
                </Text>
              ))}
            </div>
            <div className='absolute inset-0'>
              <Skeleton />
            </div>
          </>
        )}
      </div>
      <Divider kind='Hairline' />
    </>
  ) : isError ? (
    <>
      <div className='relative'>
        <Text size={'M'} color='Fire'>
          {t('orders:orderSummary:ppaError')}
          <span className='cursor-pointer' onClick={refetchPpa}>
            <Text size={'M'} color='Fire' underline renderAs='span' bold>
              {t('orders:orderSummary:tryAgain')}
            </Text>
          </span>
        </Text>
      </div>
      <Divider kind='Hairline' />
    </>
  ) : null
}
